import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from './Pagination';
import useDebouncedValue from '../hooks/useDebouncedValue';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function actionTone(action) {
  if (action.startsWith('login.failed')) return 'block';
  if (action.startsWith('login') || action.startsWith('logout')) return 'pass';
  if (action.startsWith('DELETE')) return 'block';
  return 'pending';
}

export default function AuditPanel({ onError }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ actor: '', action: '', q: '' });

  const applied = useDebouncedValue(JSON.stringify(filters), 400);

  useEffect(() => {
    setPage(1);
  }, [applied, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const parsed = JSON.parse(applied);
      const res = await axios.get('/api/audit', {
        params: {
          page,
          pageSize,
          actor: parsed.actor.trim() || undefined,
          action: parsed.action.trim() || undefined,
          q: parsed.q.trim() || undefined,
        },
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (changes) => setFilters({ ...filters, ...changes });

  return (
    <div className="row g-3 align-items-start mt-1">
      <div className="col-12">
    <div className="app-editor-card compact-card">
      <div className="app-editor-card-body compact-card-body">
        <div className="clicks-head">
          <div>
            <div className="stats-section-title">Журнал дій</div>
            <div className="stats-section-subtitle">
              {loading ? 'Завантаження…' : `${total.toLocaleString('uk-UA')} записів`}
            </div>
          </div>
        </div>

        <div className="filters-grid mb-3">
          <div className="f-field">
            <span className="f-label">Хто</span>
            <div className="f-control">
              <input
                className="f-input"
                placeholder="логін"
                value={filters.actor}
                onChange={(e) => patch({ actor: e.target.value })}
              />
            </div>
          </div>

          <div className="f-field">
            <span className="f-label">Дія</span>
            <div className="f-control">
              <input
                className="f-input"
                placeholder="login, POST /app, DELETE"
                value={filters.action}
                onChange={(e) => patch({ action: e.target.value })}
              />
            </div>
          </div>

          <div className="f-field f-span-2">
            <span className="f-label">Пошук за описом, об’єктом, деталями або IP</span>
            <div className="f-control">
              <input
                className="f-input"
                placeholder="com.example.app, 8.34.208.0/20, 1.2.3.4"
                value={filters.q}
                onChange={(e) => patch({ q: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="clicks-scroll">
          <table className="clicks-table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Час</th>
                <th style={{ width: 130 }}>Хто</th>
                <th style={{ width: 230 }}>Що зроблено</th>
                <th style={{ width: 190 }}>Об’єкт</th>
                <th style={{ width: 170 }}>Дія</th>
                <th>Деталі</th>
                <th style={{ width: 130 }}>IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <span className="cell-clip is-mono" style={{ maxWidth: 130 }}>
                      {formatDate(entry.createdAt)}
                    </span>
                  </td>
                  <td>
                    <span className="cell-clip" style={{ maxWidth: 130 }} title={entry.actor}>
                      {entry.actor}
                    </span>
                  </td>
                  <td>
                    <span className="cell-clip" style={{ maxWidth: 230 }} title={entry.summary || entry.action}>
                      <span className={`click-log-dot is-${actionTone(entry.action)}`} />
                      {entry.summary || entry.action}
                    </span>
                  </td>
                  <td>
                    <span className="cell-clip is-mono" style={{ maxWidth: 190 }} title={entry.target}>
                      {entry.target || '—'}
                    </span>
                  </td>
                  <td>
                    <span className="cell-clip is-mono" style={{ maxWidth: 170 }} title={entry.action}>
                      {entry.action}
                    </span>
                  </td>
                  <td>
                    <span className="cell-clip is-mono" style={{ maxWidth: 420 }} title={entry.details}>
                      {entry.details || '—'}
                    </span>
                  </td>
                  <td>
                    <span className="cell-clip is-mono" style={{ maxWidth: 130 }} title={entry.ip}>
                      {entry.ip || '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && !items.length ? <div className="settings-empty">Записів не знайдено</div> : null}

        <div className="clicks-foot">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]}
            disabled={loading}
          />
        </div>
      </div>
    </div>
      </div>
    </div>
  );
}
